import { cpfMask } from "utils/cpf";
import { ICarteirinha } from "./types";

export type CarteirinhaColumn = {
  field: keyof ICarteirinha;
  header: string;
  width: number;
  // Colunas de status viram <Chip> na tabela; no CSV saem como texto puro.
  isStatus?: boolean;
  format: (row: ICarteirinha) => string;
};

const text = (value: string | null) => value || "-";

// vigencyStart chega como "YYYY-MM-DD". Reformatar por split evita que o
// timezone empurre a data para o dia anterior, como `new Date` faria.
const formatVigency = (value: string | null) => {
  if (!value) return "-";
  const [y, m, d] = value.split("-");
  return d && m && y ? `${d}/${m}/${y}` : value;
};

const formatPrice = (value: number | null) =>
  value == null
    ? "-"
    : Number(value).toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

export const formatSituacao = (r: ICarteirinha): string => {
  // Backend antigo nao manda `current`: sem dado, nao afirmar nada.
  if (r.current == null) return "-";
  if (r.current && !r.exitDate) return "Vigente";
  if (r.current && r.exitDate) return `Sai em ${formatVigency(r.exitDate)}`;
  return r.exitDate ? `Saiu em ${formatVigency(r.exitDate)}` : "Encerrado";
};

export const formatNaPlataforma = (r: ICarteirinha): string =>
  r.naPlataforma == null ? "-" : r.naPlataforma ? "Sim" : "Fora da plataforma";

export type FiltroContrato = "plataforma" | "todos";

// `!== false`: o backend antigo, sem o campo, nao esconde nada.
export const aplicarFiltroContrato = (data: ICarteirinha[], filtro: FiltroContrato): ICarteirinha[] =>
  filtro === "plataforma" ? data.filter((r) => r.naPlataforma !== false) : data;

export const contarOcultas = (data: ICarteirinha[], filtro: FiltroContrato): number =>
  data.length - aplicarFiltroContrato(data, filtro).length;

// why: F4, ruling de 28/10. Filtro e contagem so fazem sentido com alguma linha
// classificada; tudo null/ausente (backend antigo ou leituras falhas) nao mostra nenhum.
export const temClassificacao = (data: ICarteirinha[]): boolean =>
  data.some((r) => typeof r.naPlataforma === "boolean");

// Cor do chip de status: so "Vigente"/"Ativo" exatos sao sucesso ("Inativo" nao).
export const chipColor = (value: string): "success" | "warning" | "default" => {
  const v = value.trim().toLowerCase();
  if (v === "vigente" || v === "ativo" || v === "sim") return "success";
  if (v.startsWith("sai em")) return "warning";
  return "default";
};

export const sortCarteirinhas = (data: ICarteirinha[]): ICarteirinha[] =>
  // `=== true` evita NaN quando `current` falta (backend antigo); sort e estavel.
  [...data].sort((a, b) => Number(b.current === true) - Number(a.current === true));

// Chave de ordenacao da coluna Situacao: posto + data ISO (nao o texto dd/mm/aaaa).
const SITUACAO_RANK: Record<string, number> = { Vigente: 0, "Sai em": 1, "Saiu em": 2, Encerrado: 3 };
export const situacaoSortKey = (r: ICarteirinha): string => {
  const label = formatSituacao(r);
  const rank = SITUACAO_RANK[label.replace(/ \d.*$/, "")] ?? 4;
  return `${rank}|${rank === 1 || rank === 2 ? r.exitDate ?? "" : ""}`;
};

export const CARTEIRINHA_COLUMNS: CarteirinhaColumn[] = [
  { field: "cpf", header: "CPF", width: 130, format: (r) => (r.cpf ? cpfMask(r.cpf) : "-") },
  // CPF do titular — ao lado do CPF; ordenar por ele agrupa a familia
  // (titular + dependentes).
  { field: "cpfTitular", header: "CPF Titular", width: 130, format: (r) => (r.cpfTitular ? cpfMask(r.cpfTitular) : "-") },
  { field: "fullName", header: "Nome", width: 220, format: (r) => text(r.fullName) },
  { field: "cardNumber", header: "Carteirinha", width: 180, format: (r) => text(r.cardNumber) },
  { field: "kinship", header: "Parentesco", width: 120, format: (r) => text(r.kinship) },
  { field: "operatorName", header: "Operadora", width: 200, format: (r) => text(r.operatorName) },
  { field: "planName", header: "Plano", width: 200, format: (r) => text(r.planName) },
  { field: "planPrice", header: "Valor", width: 110, format: (r) => formatPrice(r.planPrice) },
  { field: "contractCode", header: "Cód. Contrato", width: 130, format: (r) => text(r.contractCode) },
  { field: "naPlataforma", header: "Contrato na plataforma", width: 170, isStatus: true, format: formatNaPlataforma },
  { field: "vigencyStart", header: "Vigência", width: 120, format: (r) => formatVigency(r.vigencyStart) },
  // StatusBeneficiario — status do colaborador/pessoa na operadora.
  { field: "beneficiaryStatus", header: "Status Colaborador", width: 160, isStatus: true, format: (r) => text(r.beneficiaryStatus) },
  // StatusBeneficiarioOperadora — status da operadora/contrato.
  { field: "operatorBeneficiaryStatus", header: "Status Operadora", width: 160, isStatus: true, format: (r) => text(r.operatorBeneficiaryStatus) },
  // Situação do vínculo na operadora (spec 2026-09-29, L1): vigente, sai em, saiu em.
  { field: "situacao", header: "Situação", width: 190, isStatus: true, format: formatSituacao },
];
